import React from 'react';
import PropTypes from 'prop-types';
import { format as formatDate } from 'date-fns';

/**
 * Formatea valor como moneda chilena (CLP).
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Modal de formulario para crear o editar una cita.
 *
 * Props:
 *  - isOpen: boolean
 *  - mode: 'create' | 'edit'
 *  - onClose: callback para cerrar
 *  - onSubmit: callback (e) => void, handler del submit
 *  - formData: estado del formulario
 *  - setFormData: setter del estado del formulario
 *  - pacientes: array de pacientes [{ id, rut, nombre }]
 *  - tratamientos: array de tratamientos [{ id, nombre, precio, duracion_minutos }]
 *  - profesionales: array de profesionales [{ id, first_name, last_name, rol }]
 *  - horariosDisponibles: array de strings con horas disponibles ["09:00", ...]
 *  - horasOcupadas: array de strings con horas ocupadas
 *  - selectedDate: Date para mostrar en el título (modo create)
 *  - originalHora: string, hora original en modo edición
 *  - citas: array de citas (solo necesario en modo edit para lógica de bloqueo extendido)
 *  - getDuracionFromTratamiento: fn(formData) => número de minutos
 */
const CitaFormModal = ({
  isOpen,
  mode,
  onClose,
  onSubmit,
  formData,
  setFormData,
  pacientes,
  tratamientos,
  profesionales,
  horariosDisponibles,
  horasOcupadas,
  selectedDate,
  originalHora,
  citas,
  getDuracionFromTratamiento,
}) => {
  if (!isOpen) return null;

  const isEdit = mode === 'edit';

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ── Renderizado de time slots ──────────────────────────────────────
  const renderTimeSlots = () => {
    if (isEdit) {
      // ── Modo edición: time slots con lógica de hora original y bloqueo extendido ──
      const uniqueHorarios = [...new Set([...horariosDisponibles, originalHora].filter(Boolean))].sort();
      return (
        <div className="horarios-grid">
          {uniqueHorarios.map((hora) => {
            // Normalizar formato
            let horaFormateada = hora;
            if (horaFormateada && horaFormateada.includes(':')) {
              const partes = horaFormateada.split(':');
              if (partes.length >= 2) {
                horaFormateada = `${partes[0]}:${partes[1]}`;
              }
            }

            const isOriginalHora = horaFormateada === originalHora;
            const isSelected = horaFormateada === formData.hora;

            // Verificar si la hora está bloqueada por cita extendida
            const esBloqueadaPorExtendida = horasOcupadas.some((horaOcupada) => {
              if (horaOcupada === originalHora) return false;
              const [horaOcupadaH, horaOcupadaM] = horaOcupada.split(':').map(Number);
              const [horaActualH, horaActualM] = horaFormateada.split(':').map(Number);

              if (horaOcupadaH === horaActualH - 1 && horaOcupadaM === horaActualM) {
                const citaExtendida = (citas || []).find((c) => {
                  let horaC = c.hora;
                  if (horaC && horaC.includes(':')) {
                    const partes = horaC.split(':');
                    if (partes.length >= 2) {
                      horaC = `${partes[0]}:${partes[1]}`;
                    }
                  }
                  return (
                    horaC === horaOcupada &&
                    (c.tratamiento?.duracion_minutos || 60) >= 120 &&
                    c.id !== formData.id
                  );
                });
                return !!citaExtendida;
              }
              return false;
            });

            // Verificar siguiente hora disponible (para duración extendida)
            const [horaH, horaM] = horaFormateada.split(':').map(Number);
            const siguienteHora = `${(horaH + 1).toString().padStart(2, '0')}:${horaM.toString().padStart(2, '0')}`;
            const siguienteHoraDisponible =
              horariosDisponibles.includes(siguienteHora) || siguienteHora === originalHora;

            const isOcupada =
              (horasOcupadas.includes(horaFormateada) && !isOriginalHora) || esBloqueadaPorExtendida;
            const noPermiteDuracionExtendida =
              (getDuracionFromTratamiento && getDuracionFromTratamiento(formData) >= 120) &&
              !siguienteHoraDisponible &&
              !isOriginalHora;

            const isDisabled = isOcupada || noPermiteDuracionExtendida;

            if (isDisabled && !isOriginalHora) return null;

            // Auto-deselect si la hora seleccionada se vuelve inválida por duración extendida
            if (
              formData.hora === horaFormateada &&
              getDuracionFromTratamiento &&
              getDuracionFromTratamiento(formData) >= 120 &&
              !siguienteHoraDisponible &&
              horaFormateada !== originalHora
            ) {
              setTimeout(() => {
                setFormData((prev) => ({ ...prev, hora: '' }));
              }, 100);
            }

            // Clases CSS
            let btnClass = 'py-2 px-3 rounded-md text-center transition-colors ';
            if (isSelected) {
              btnClass += 'bg-accent text-white ';
            } else if (isOriginalHora && !isSelected) {
              btnClass += 'bg-yellow-100 text-gray-800 border border-yellow-500 hover:bg-yellow-200 ';
            } else {
              btnClass += 'bg-gray-100 text-gray-800 hover:bg-gray-200 ';
            }

            return (
              <button
                key={horaFormateada}
                type="button"
                className={btnClass}
                onClick={() => setFormData({ ...formData, hora: horaFormateada })}
                title={isOriginalHora ? 'Hora original de la cita' : 'Hora disponible'}
              >
                {horaFormateada}
                {isOriginalHora && !isSelected && ' (original)'}
              </button>
            );
          })}
          {getDuracionFromTratamiento && getDuracionFromTratamiento(formData) >= 120 && (
            <p className="mt-2 text-sm text-amber-600 col-span-full">
              ⓘ Las horas en gris no están disponibles para citas de 2 horas porque la siguiente hora está ocupada.
            </p>
          )}
        </div>
      );
    }

    // ── Modo creación: time slots simples ──
    return (
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Horarios Disponibles</label>
        <div className="grid grid-cols-3 gap-2">
          {horariosDisponibles.map((hora) => {
            const isSelected = formData.hora === hora;
            const noDisponible = horasOcupadas.includes(hora);

            let buttonStyle = isSelected
              ? 'bg-accent text-white'
              : noDisponible
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200';

            return (
              <div key={hora} className="relative group">
                <button
                  type="button"
                  className={`w-full py-2 px-3 rounded-md text-center transition-colors ${buttonStyle}`}
                  onClick={() => setFormData({ ...formData, hora })}
                  disabled={noDisponible}
                >
                  {hora}
                </button>
                {noDisponible && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Hora no disponible
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Renderizado principal ───────────────────────────────────────────
  if (isEdit) {
    // ── Modal de edición ──
    return (
      <div
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header con gradiente Teal */}
          <div className="bg-gradient-to-r from-[#2A9D8F] to-[#227f74] px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Editar Cita</h3>
                <p className="text-white/80 text-sm">ID: {formData.id}</p>
              </div>
              <button
                className="text-white/80 hover:text-white transition-colors p-1"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <form onSubmit={onSubmit} className="p-6">
            {/* Paciente */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Paciente</label>
              <select
                value={formData.paciente_rut}
                onChange={(e) => setFormData({ ...formData, paciente_rut: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 py-2 px-3"
                required
              >
                <option value="">Seleccione un paciente</option>
                {pacientes.map((paciente) => (
                  <option key={paciente.id} value={paciente.rut}>
                    {paciente.nombre} - {paciente.rut}
                  </option>
                ))}
              </select>
            </div>

            {/* Tratamiento */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tratamiento</label>
              <select
                value={formData.tipo_tratamiento}
                onChange={(e) => setFormData({ ...formData, tipo_tratamiento: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 py-2 px-3"
                required
              >
                <option value="">Seleccione un tratamiento</option>
                {tratamientos.map((trat) => (
                  <option key={trat.id} value={trat.nombre}>
                    {trat.nombre} - {formatCurrency(trat.precio)}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha (yyyy-mm-dd)</label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 py-2 px-3"
                required
              />
            </div>

            {/* Estado */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className={`block w-full rounded-lg border-2 shadow-sm py-2.5 px-3 transition-colors ${
                  formData.estado === 'completada'
                    ? 'border-green-400 bg-green-50 focus:border-green-500 focus:ring-green-500'
                    : formData.estado === 'cancelada'
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-[#2A9D8F] focus:ring-[#2A9D8F]'
                }`}
                required
              >
                <option value="reservada">📅 Reservada</option>
                <option value="confirmada">✅ Confirmada</option>
                <option value="completada">🎉 Completada</option>
                <option value="cancelada">❌ Cancelada</option>
              </select>
              {formData.estado === 'completada' && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800 flex items-start gap-2">
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>
                      <strong>Nota:</strong> Para registrar los insumos utilizados, crea la ficha clínica después de completar la cita.
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Duración */}
            {formData.tratamiento_id && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Duración de la cita</label>
                <p className="text-sm text-gray-500">
                  {(tratamientos.find((t) => t.id === formData.tratamiento_id)?.duracion_minutos || 60)} minutos
                  <span className="text-xs text-gray-400 ml-1">(según tratamiento)</span>
                </p>
              </div>
            )}

            {/* Horarios */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Horarios Disponibles</label>
              {renderTimeSlots()}
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4 mt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#227f74] font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={!formData.hora || !formData.paciente_rut || !formData.tipo_tratamiento}
              >
                Actualizar Cita
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── Modal de creación ──
  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start sm:items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-md sm:max-w-lg bg-white rounded-lg shadow-xl sm:my-8">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 pr-4">
              <span className="hidden sm:inline">
                Nueva Cita - {selectedDate ? formatDate(selectedDate, 'dd/MM/yyyy') : 'Fecha no seleccionada'}
              </span>
              <span className="sm:hidden">
                Nueva Cita<br />
                <span className="text-sm font-normal text-gray-600">
                  {selectedDate ? formatDate(selectedDate, 'dd/MM/yyyy') : 'Fecha no seleccionada'}
                </span>
              </span>
            </h3>
            <button
              className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100"
              style={{ minHeight: '44px', minWidth: '44px' }}
              onClick={onClose}
              aria-label="Cerrar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={onSubmit}>
            {/* Profesional (Opcional) */}
            {profesionales.length > 1 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profesional <span className="text-gray-400 font-normal">(Opcional)</span>
                </label>
                <select
                  value={formData.profesional}
                  onChange={(e) => setFormData({ ...formData, profesional: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 py-2 px-3"
                >
                  <option value="">Cualquier profesional</option>
                  {profesionales.map((prof) => (
                    <option key={prof.id} value={prof.id}>
                      {prof.first_name} {prof.last_name} {prof.rol === 'admin' ? '(Admin)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Paciente */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Paciente</label>
              <select
                value={formData.paciente_rut}
                onChange={(e) => setFormData({ ...formData, paciente_rut: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 py-2 px-3"
                required
              >
                <option value="">Seleccione un paciente</option>
                {pacientes.map((paciente) => (
                  <option key={paciente.id} value={paciente.rut}>
                    {paciente.nombre} - {paciente.rut}
                  </option>
                ))}
              </select>
            </div>

            {/* Tratamiento */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tratamiento</label>
              <select
                value={formData.tipo_tratamiento}
                onChange={(e) => setFormData({ ...formData, tipo_tratamiento: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 py-2 px-3"
                required
              >
                <option value="">Seleccione un tratamiento</option>
                {tratamientos.map((trat) => (
                  <option key={trat.id} value={trat.nombre}>
                    {trat.nombre} - {formatCurrency(trat.precio)}
                  </option>
                ))}
              </select>
            </div>

            {/* Duración */}
            {formData.tratamiento_id && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Duración de la cita</label>
                <p className="text-sm text-gray-500">
                  {(tratamientos.find((t) => t.id === formData.tratamiento_id)?.duracion_minutos || 60)} minutos
                  <span className="text-xs text-gray-400 ml-1">(según tratamiento)</span>
                </p>
              </div>
            )}

            {/* Horarios Disponibles */}
            {renderTimeSlots()}

            {/* Sprint 4: Plan de sesiones (solo en modo creación) */}
            {!isEdit && (
              <div className="mb-4 mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.plan_sesiones_total}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, plan_sesiones_total: 4, plan_descuento: 0 });
                      } else {
                        setFormData({ ...formData, plan_sesiones_total: null, plan_descuento: null });
                      }
                    }}
                    className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-purple-800">📋 Crear como plan de sesiones</span>
                </label>
                
                {formData.plan_sesiones_total && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-purple-700 mb-1">
                        N° de sesiones
                      </label>
                      <select
                        value={formData.plan_sesiones_total}
                        onChange={(e) => setFormData({ ...formData, plan_sesiones_total: parseInt(e.target.value) })}
                        className="block w-full rounded-md border-purple-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 py-1.5 px-2 text-sm"
                      >
                        {[2, 4, 6, 8, 10, 12].map((n) => (
                          <option key={n} value={n}>{n} sesiones</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-purple-700 mb-1">
                        Descuento (%)
                      </label>
                      <select
                        value={formData.plan_descuento || 0}
                        onChange={(e) => setFormData({ ...formData, plan_descuento: parseInt(e.target.value) })}
                        className="block w-full rounded-md border-purple-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 py-1.5 px-2 text-sm"
                      >
                        <option value={0}>0%</option>
                        <option value={10}>10%</option>
                        <option value={15}>15%</option>
                        <option value={20}>20%</option>
                        <option value={25}>25%</option>
                        <option value={30}>30%</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botones */}
            <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
                style={{ minHeight: '44px' }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-3 bg-accent text-white rounded-md hover:bg-accent-dark font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                style={{ minHeight: '44px' }}
                disabled={!formData.hora || !formData.paciente_rut || !formData.tipo_tratamiento}
              >
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

CitaFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  pacientes: PropTypes.array.isRequired,
  tratamientos: PropTypes.array.isRequired,
  profesionales: PropTypes.array,
  horariosDisponibles: PropTypes.array,
  horasOcupadas: PropTypes.array,
  selectedDate: PropTypes.instanceOf(Date),
  originalHora: PropTypes.string,
  citas: PropTypes.array,
  getDuracionFromTratamiento: PropTypes.func,
};

CitaFormModal.defaultProps = {
  profesionales: [],
  horariosDisponibles: [],
  horasOcupadas: [],
  selectedDate: null,
  originalHora: '',
  citas: [],
  getDuracionFromTratamiento: () => 60,
};

export default CitaFormModal;
